import { MERGE_LADDER, SCATTER_SYMBOL_ID, SECRET_BONUS_SYMBOL_ID } from "../assets/mergeLadder";

/**
 * The merge mechanic's rules, as the offline rounds are built and checked against them (the game's rules document,
 * `merge` mechanic): what a backend computes and the client only presents. Pure functions over a board of symbol ids,
 * row-major (row 0 at the top), linear index = row × columns + column.
 */

/** A board: `rows[row][column]`; `null` is a cell a merge consumed, before gravity fills it. */
export type Board = (string | null)[][];

/** 4 or more orthogonally connected cells of one symbol (never the scatter, never the Secret Bonus). */
export interface Cluster {
  symbolId: string;
  /** Linear indices, ascending. */
  cells: number[];
}

/** One merge output: the target keeps its cell and becomes `resultSymbolId`; the consumed cells leave the board. */
export interface MergeOutput {
  target: number;
  resultSymbolId: string;
  consumed: number[];
}

export const MIN_CLUSTER_SIZE = 4;
/** Four outputs is a hard cap. */
export const MAX_MERGE_OUTPUTS = 4;

/** The symbol a merge of `symbolId` produces: one step up the ladder (H4 gives the scatter). */
export function nextOnLadder(symbolId: string): string {
  const step = MERGE_LADDER.indexOf(symbolId as (typeof MERGE_LADDER)[number]);
  if (step < 0 || step === MERGE_LADDER.length - 1) throw new Error(`nextOnLadder: "${symbolId}" does not merge`);
  return MERGE_LADDER[step + 1];
}

/** How many outputs a cluster of `size` produces: one per pair, at most four (an H1 cluster is the exception, one FS). */
export function mergeOutputCount(symbolId: string, size: number): number {
  return symbolId === "H1" ? 1 : Math.min(Math.floor(size / 2), MAX_MERGE_OUTPUTS);
}

export function columnsOf(board: Board): number {
  return board[0]?.length ?? 0;
}

export function symbolAt(board: Board, index: number): string | null {
  const columns = columnsOf(board);
  return board[Math.floor(index / columns)][index % columns];
}

/** The four orthogonal neighbours of `index` inside the board. */
export function neighbours(board: Board, index: number): number[] {
  const columns = columnsOf(board);
  const rows = board.length;
  const row = Math.floor(index / columns);
  const col = index % columns;
  const out: number[] = [];
  if (row > 0) out.push(index - columns);
  if (row < rows - 1) out.push(index + columns);
  if (col > 0) out.push(index - 1);
  if (col < columns - 1) out.push(index + 1);
  return out;
}

/** Every payable cluster on the board, in the order of their lowest cell. */
export function findClusters(board: Board): Cluster[] {
  const columns = columnsOf(board);
  const size = board.length * columns;
  const seen = new Set<number>();
  const clusters: Cluster[] = [];
  for (let start = 0; start < size; start++) {
    const symbolId = symbolAt(board, start);
    if (seen.has(start) || symbolId === null || symbolId === SCATTER_SYMBOL_ID || symbolId === SECRET_BONUS_SYMBOL_ID) continue;
    const cells: number[] = [];
    const queue = [start];
    seen.add(start);
    while (queue.length > 0) {
      const index = queue.shift()!;
      cells.push(index);
      for (const next of neighbours(board, index)) {
        if (!seen.has(next) && symbolAt(board, next) === symbolId) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    if (cells.length >= MIN_CLUSTER_SIZE) clusters.push({ symbolId, cells: cells.sort((a, b) => a - b) });
  }
  return clusters;
}

/**
 * The offline server's choice of where a cluster's outputs land (the rules let the server choose): neighbouring candies
 * pair up and each pair merges into one, the consumed candy stepping into its neighbour, so no candy crosses the board.
 * The cells are taken from the bottom row up, left to right, each pairing with the candy above it (which drops into it)
 * or else the one to its right (which slides into it), so the merged symbols stay low and feed the next cluster; the
 * cells past the outputs' count are leftovers. A cluster whose candies cannot make up the count as neighbouring pairs
 * (a plus sign: four arms around one centre) is not one the offline server plays: the rules leave its merge open.
 * An H1 cluster lands its one FS on its most central cell, every other cell flying into it.
 */
export function chooseMergeOutputs(board: Board, cluster: Cluster): MergeOutput[] {
  const columns = columnsOf(board);
  if (cluster.symbolId === "H1") {
    const target = centralCell(cluster.cells, columns);
    return [{ target, resultSymbolId: SCATTER_SYMBOL_ID, consumed: cluster.cells.filter((index) => index !== target) }];
  }
  const resultSymbolId = nextOnLadder(cluster.symbolId);
  const count = mergeOutputCount(cluster.symbolId, cluster.cells.length);
  const pairs = neighbourPairs(cluster.cells, columns, count);
  if (pairs.length < count) {
    throw new Error(`chooseMergeOutputs: the ${cluster.symbolId} cluster at ${cluster.cells.join(",")} cannot make ${count} pairs of neighbours`);
  }
  return pairs.map(([target, consumed]) => ({ target, resultSymbolId, consumed: [consumed] }));
}

/**
 * Up to `count` disjoint pairs of orthogonal neighbours, `[target, consumed]`: the first found taking the cells bottom
 * row first, left to right, each with the cell above it, else the one to its right, else leaving it over. Every
 * pairing is reachable that way (a pair's later cell is above or right of its earlier one), so fewer than `count`
 * means the shape has no more.
 */
function neighbourPairs(cells: readonly number[], columns: number, count: number): Array<[number, number]> {
  const order = [...cells].sort((a, b) => Math.floor(b / columns) - Math.floor(a / columns) || (a % columns) - (b % columns));
  const inCluster = new Set(cells);
  const taken = new Set<number>();
  const pairs: Array<[number, number]> = [];
  let best: Array<[number, number]> = [];
  const search = (at: number): boolean => {
    if (pairs.length > best.length) best = [...pairs];
    if (pairs.length === count) return true;
    const free = order.slice(at).filter((cell) => !taken.has(cell)).length;
    if (pairs.length + Math.floor(free / 2) < count) return false;
    const cell = order[at];
    if (taken.has(cell)) return search(at + 1);
    const above = cell - columns;
    const right = cell % columns < columns - 1 ? cell + 1 : -1;
    for (const partner of [above, right]) {
      if (!inCluster.has(partner) || taken.has(partner)) continue;
      taken.add(cell).add(partner);
      pairs.push([cell, partner]);
      if (search(at + 1)) return true;
      pairs.pop();
      taken.delete(cell);
      taken.delete(partner);
    }
    return search(at + 1);
  };
  search(0);
  return best;
}

/** The cell with the least total distance to the others (ties: the lowest, then the leftmost): where a cluster converges. */
function centralCell(cells: readonly number[], columns: number): number {
  const row = (index: number): number => Math.floor(index / columns);
  const col = (index: number): number => index % columns;
  const spread = (index: number): number => cells.reduce((sum, other) => sum + Math.abs(row(index) - row(other)) + Math.abs(col(index) - col(other)), 0);
  return [...cells].sort((a, b) => spread(a) - spread(b) || row(b) - row(a) || col(a) - col(b))[0];
}

/** The board after the merges: every target its result, every consumed cell empty. */
export function applyMerges(board: Board, outputs: readonly MergeOutput[]): Board {
  const columns = columnsOf(board);
  const next = board.map((row) => [...row]);
  for (const output of outputs) {
    next[Math.floor(output.target / columns)][output.target % columns] = output.resultSymbolId;
    for (const index of output.consumed) next[Math.floor(index / columns)][index % columns] = null;
  }
  return next;
}

/**
 * Gravity: every surviving symbol falls straight down its column, keeping its identity; the vacated top cells take
 * the next symbols of the column's `refill` queue — the symbols stacked above the grid, the first one closest to it,
 * so it lands lowest. The queue is consumed in place.
 */
export function applyGravity(board: Board, refill: string[][]): string[][] {
  const rows = board.length;
  const columns = columnsOf(board);
  const next: string[][] = Array.from({ length: rows }, () => Array.from({ length: columns }, () => ""));
  for (let col = 0; col < columns; col++) {
    const survivors: string[] = [];
    for (let row = rows - 1; row >= 0; row--) {
      const symbolId = board[row][col];
      if (symbolId !== null) survivors.push(symbolId);
    }
    let row = rows - 1;
    for (const symbolId of survivors) next[row--][col] = symbolId;
    for (; row >= 0; row--) {
      const symbolId = refill[col].shift();
      if (symbolId === undefined) throw new Error(`applyGravity: column ${col} ran out of refill symbols`);
      next[row][col] = symbolId;
    }
  }
  return next;
}

/** The cell multiplier badges after a win: x2 on a cell's first win, +1 on each later one. */
export function applyBadges(badges: ReadonlyMap<number, number>, winningCells: Iterable<number>): Map<number, number> {
  const next = new Map(badges);
  for (const index of winningCells) next.set(index, (next.get(index) ?? 1) + 1);
  return next;
}

/** A cluster's multiplier: its cells' badges added together, or 1 when none of its cells carries one. */
export function clusterMultiplier(badges: ReadonlyMap<number, number>, cells: readonly number[]): number {
  let sum = 0;
  for (const index of cells) sum += badges.get(index) ?? 0;
  return sum > 0 ? sum : 1;
}

/** A grid-length "0/1" mask of `indices`. */
export function maskOf(indices: Iterable<number>, size: number): string {
  const set = new Set(indices);
  return Array.from({ length: size }, (_, i) => (set.has(i) ? "1" : "0")).join("");
}
